import { CommonModule } from '@angular/common';
import {
    ChangeDetectorRef,
    Component,
    Inject,
    Input,
    ViewEncapsulation,
    type AfterViewInit,
    signal,
} from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { TranslocoService } from '@ngneat/transloco';
import { AIForImageService } from 'app/api';
import { LoadingService } from 'app/core/loading-spinner/loading.service';
import { APP_CONFIGURATION_TOKEN } from 'app/injection-token';
import { FrontendAppConfiguration } from 'app/services/configuration.services';
import { XmlInterceptor } from 'app/services/xml/xml-interceptor.service';
import {
    Alignment,
    AutoImage,
    AutoLink,
    Autoformat,
    Autosave,
    BlockQuote,
    Bold,
    ClassicEditor,
    Code,
    Essentials,
    FindAndReplace,
    FontBackgroundColor,
    FontColor,
    FontFamily,
    FontSize,
    GeneralHtmlSupport,
    Heading,
    Highlight,
    HorizontalLine,
    ImageBlock,
    ImageCaption,
    ImageInline,
    ImageInsert,
    ImageInsertViaUrl,
    ImageResize,
    ImageStyle,
    ImageTextAlternative,
    ImageToolbar,
    ImageUpload,
    Indent,
    IndentBlock,
    Italic,
    Link,
    LinkImage,
    List,
    ListProperties,
    MediaEmbed,
    PageBreak,
    Paragraph,
    PasteFromOffice,
    RemoveFormat,
    SimpleUploadAdapter,
    SpecialCharacters,
    SpecialCharactersArrows,
    SpecialCharactersCurrency,
    SpecialCharactersEssentials,
    SpecialCharactersLatin,
    SpecialCharactersMathematical,
    SpecialCharactersText,
    Strikethrough,
    Style,
    Subscript,
    Superscript,
    Table,
    TableCaption,
    TableCellProperties,
    TableColumnResize,
    TableProperties,
    TableToolbar,
    TextTransformation,
    TodoList,
    Underline,
    type EditorConfig,
} from 'ckeditor5';
import { Observable, Subject, takeUntil, tap } from 'rxjs';
import { AIDButton } from './plugins/aidButton.plugin';
import { AIQPlugin } from './plugins/aiqButton.plugin';
import { DialogBuilderService } from 'app/core/dialogBuilder/dialog-builder.service';
import { AiqDialogComponent } from './plugins/aiq-dialog/aiq-dialog.component';
import { ButtonType } from 'app/core/dialogBuilder/dialog-builder.models';
import { MatDialogRef } from '@angular/material/dialog';
import { TransactionAIPlugin } from './plugins/transactionAiButton.plugin';
import { DescriptionAiApiService } from 'app/modules/task/ai.service';
import { AIFPlugin } from './plugins/aifButton.plugin';
import { AifDialogComponent } from './plugins/aif-dialog/aif-dialog.component';

/**
 * Create a free account with a trial: https://portal.ckeditor.com/checkout?plan=free
 */
const LICENSE_KEY = 'GPL'; // or <YOUR_LICENSE_KEY>.

@Component({
    selector: 'app-root',
    standalone: true,
    imports: [CommonModule, CKEditorModule, FormsModule, ReactiveFormsModule],
    templateUrl: './ck-editor.component.html',
    styleUrls: ['./ck-editor.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class CkEditorComponent implements AfterViewInit {
    private _destroy: Subject<void> = new Subject();

    constructor(
        private changeDetector: ChangeDetectorRef,
        private _translocoService: TranslocoService,
        private xmlInterceptor: XmlInterceptor,
        private aiImageService: AIForImageService,
        private _loadingService: LoadingService,
        private dialogService: DialogBuilderService,
        private transactionAiService: DescriptionAiApiService,
        @Inject(APP_CONFIGURATION_TOKEN) private appConfig: FrontendAppConfiguration
    ) {}

    @Input() formGroup: FormGroup;
    @Input() formControlName: string;
    @Input() showTransactionAiButton: boolean = false;
    @Input() showAifButton: boolean = false;

    public isLayoutReady = false;
    public Editor = ClassicEditor;
    public config: EditorConfig = {}; // CKEditor needs the DOM tree before calculating the configuration.

    ngOnInit(): void {}

    public ngAfterViewInit(): void {
        this.initEditorConfig();

        // Listen for language changes
        this._translocoService.langChanges$.pipe(takeUntil(this._destroy)).subscribe(newLang => {
            this.changeLanguage(newLang);
        });

        this.isLayoutReady = true;
        this.changeDetector.detectChanges();
    }
    ngOnDestroy(): void {
        this._destroy.next();
        this._destroy.complete();
    }

    private initEditorConfig(): void {
        const toolbarItems = [
            'findAndReplace',
            '|',
            'heading',
            'fontFamily',
            'fontSize',
            '|',
            'aiqButton',
            '|',
            'bold',
            'italic',
            'underline',
            'strikethrough',
            'fontColor',
            'fontBackgroundColor',
            '|',
            'link',
            'insertTable',
            'imageUpload',
            'mediaEmbed',
            '|',
            'alignment',
            'bulletedList',
            'numberedList',
            'outdent',
            'indent',
            '|',
            'subscript',
            'superscript',
            '|',
            'removeFormat',
            'blockQuote',
            'horizontalLine',
            '|',
            'specialCharacters',
        ];

        if (this.showTransactionAiButton) {
            const aiqIndex = toolbarItems.indexOf('aiqButton');
            if (aiqIndex !== -1) {
                toolbarItems.splice(aiqIndex + 1, 0, 'transactionAiButton');
            } else {
                toolbarItems.push('transactionAiButton');
            }
        }
        if (this.showAifButton) {
            const aiqIndex = toolbarItems.indexOf('aiqButton');
            if (aiqIndex !== -1) {
                toolbarItems.splice(aiqIndex + 1, 0, 'aifButton');
            } else {
                toolbarItems.push('aifButton');
            }
        }

        this.config = {
            toolbar: {
                items: toolbarItems,
                shouldNotGroupWhenFull: false,
            },
            plugins: [
                Alignment,
                Autoformat,
                AutoImage,
                AutoLink,
                Autosave,
                BlockQuote,
                Bold,
                Code,
                Essentials,
                FindAndReplace,
                FontBackgroundColor,
                FontColor,
                FontFamily,
                FontSize,
                GeneralHtmlSupport,
                Heading,
                Highlight,
                HorizontalLine,
                ImageBlock,
                ImageCaption,
                ImageInline,
                ImageInsert,
                ImageInsertViaUrl,
                ImageResize,
                ImageStyle,
                ImageTextAlternative,
                ImageToolbar,
                ImageUpload,
                Indent,
                IndentBlock,
                Italic,
                Link,
                LinkImage,
                List,
                ListProperties,
                MediaEmbed,
                PageBreak,
                Paragraph,
                PasteFromOffice,
                RemoveFormat,
                SimpleUploadAdapter,
                SpecialCharacters,
                SpecialCharactersArrows,
                SpecialCharactersCurrency,
                SpecialCharactersEssentials,
                SpecialCharactersLatin,
                SpecialCharactersMathematical,
                SpecialCharactersText,
                Strikethrough,
                Style,
                Subscript,
                Superscript,
                Table,
                TableCaption,
                TableCellProperties,
                TableColumnResize,
                TableProperties,
                TableToolbar,
                TextTransformation,
                TodoList,
                Underline,
                AIDButton,
                AIQPlugin,
                TransactionAIPlugin,
                AIFPlugin,
            ],
            fontFamily: {
                supportAllValues: true,
            },
            fontSize: {
                options: [10, 12, 14, 'default', 18, 20, 22],
                supportAllValues: true,
            },
            // heading: {
            //     options: [
            //         {
            //             model: 'paragraph',
            //             title: 'Paragraph',
            //             class: 'ck-heading_paragraph',
            //         },
            //         {
            //             model: 'heading1',
            //             view: 'h1',
            //             title: 'Heading 1',
            //             class: 'ck-heading_heading1',
            //         },
            //         {
            //             model: 'heading2',
            //             view: 'h2',
            //             title: 'Heading 2',
            //             class: 'ck-heading_heading2',
            //         },
            //         {
            //             model: 'heading3',
            //             view: 'h3',
            //             title: 'Heading 3',
            //             class: 'ck-heading_heading3',
            //         },
            //         {
            //             model: 'heading4',
            //             view: 'h4',
            //             title: 'Heading 4',
            //             class: 'ck-heading_heading4',
            //         },
            //         {
            //             model: 'heading5',
            //             view: 'h5',
            //             title: 'Heading 5',
            //             class: 'ck-heading_heading5',
            //         },
            //         {
            //             model: 'heading6',
            //             view: 'h6',
            //             title: 'Heading 6',
            //             class: 'ck-heading_heading6',
            //         },
            //     ],
            // },
            htmlSupport: {
                allow: [
                    {
                        name: /^.*$/,
                        styles: true,
                        attributes: true,
                        classes: true,
                    },
                ],
            },
            image: {
                toolbar: [
                    'toggleImageCaption',
                    'imageTextAlternative',
                    '|',
                    'imageStyle:inline',
                    'imageStyle:wrapText',
                    'imageStyle:breakText',
                    '|',
                    'resizeImage',
                    'aidButton',
                ],
            },
            licenseKey: LICENSE_KEY,
            link: {
                addTargetToExternalLinks: true,
                defaultProtocol: 'https://',
                decorators: {
                    toggleDownloadable: {
                        mode: 'manual',
                        label: 'Downloadable',
                        attributes: {
                            download: 'file',
                        },
                    },
                },
            },
            list: {
                properties: {
                    styles: true,
                    startIndex: true,
                    reversed: true,
                },
            },
            style: {
                definitions: [
                    {
                        name: 'Article category',
                        element: 'h3',
                        classes: ['category'],
                    },
                    {
                        name: 'Title',
                        element: 'h2',
                        classes: ['document-title'],
                    },
                    {
                        name: 'Subtitle',
                        element: 'h3',
                        classes: ['document-subtitle'],
                    },
                    {
                        name: 'Info box',
                        element: 'p',
                        classes: ['info-box'],
                    },
                    {
                        name: 'Side quote',
                        element: 'blockquote',
                        classes: ['side-quote'],
                    },
                    {
                        name: 'Marker',
                        element: 'span',
                        classes: ['marker'],
                    },
                    {
                        name: 'Spoiler',
                        element: 'span',
                        classes: ['spoiler'],
                    },
                    {
                        name: 'Code (dark)',
                        element: 'pre',
                        classes: ['fancy-code', 'fancy-code-dark'],
                    },
                    {
                        name: 'Code (bright)',
                        element: 'pre',
                        classes: ['fancy-code', 'fancy-code-bright'],
                    },
                ],
            },
            table: {
                contentToolbar: [
                    'tableColumn',
                    'tableRow',
                    'mergeTableCells',
                    'tableProperties',
                    'tableCellProperties',
                ],
            },
            simpleUpload: {
                uploadUrl: this.appConfig.apiHost + '/backend/api/upload-file',
            },
            language: {
                ui: this._translocoService.getActiveLang(), // Set initial language
                content: this._translocoService.getActiveLang(), // Set initial language
            },
        };

        this.config['aidButtonClick'] = this.callAI.bind(this, this.formControlName);
        this.config['aiqButtonClick'] = this.openAIQDialog.bind(this);
        this.config['transactionAiButtonClick'] = this.transactionAiButton.bind(this);
        this.config['aifButtonClick'] = this.openAIFDialog.bind(this);

        this.xmlInterceptor.errorOccures$.pipe(takeUntil(this._destroy)).subscribe(() => {
            this.removeImageFailedToUpload();
        });
    }

    private changeLanguage(newLang: string): void {
        if (this.config.language !== newLang) {
            // Force update language
            this.config = { ...this.config, language: newLang };

            this.reinitializeEditor();
        }
    }

    private reinitializeEditor(): void {
        this.isLayoutReady = false;
        this.changeDetector.detectChanges();
        setTimeout(() => {
            this.isLayoutReady = true;
            this.changeDetector.detectChanges();
        });
    }

    callAI(formControlName: any, asd: Element): void {
        const fileUrl: string = asd.getAttribute('src') as string;
        this._loadingService.setLoadingStatus(true);
        this.aiImageService
            .extractText({ fileUrl })
            .pipe(takeUntil(this._destroy))
            .subscribe((response: any) => {
                let value: string = this.formGroup.value[formControlName];

                response.name.forEach(text => {
                    text?.text?.forEach(item => {
                        value += `<p>${item}</p>`;
                    });
                });

                const patchValue: any = {};
                patchValue[formControlName] = value;
                this.formGroup.patchValue(patchValue, { emitEvent: false });
                this._loadingService.setLoadingStatus(false);
            });
    }

    private removeImageFailedToUpload(): void {
        const modifiedContent = this.removeBase64Images(this.formGroup.value[this.formControlName]);
        // Update the form control with the modified content
        const patchValue: any = {};
        patchValue[this.formControlName] = modifiedContent;
        this.formGroup.patchValue(patchValue, { emitEvent: false });
    }

    removeBase64Images(content: string): string {
        // Regular expression to match <img> tags with base64 src attribute
        const regex = /<img[^>]+src="data:image\/[^;]+;base64,[^">]+"[^>]*>/g;
        // Remove matched <img> tags from the content
        return content?.replace(regex, '');
    }

    openAIQDialog(): MatDialogRef<AiqDialogComponent, any> {
        const executeActionSignal = signal(null);
        return this.dialogService.openDialog({
            descriptor: {
                header: {
                    title: 'AIQ Catalog',
                    showCloseButton: true,
                },
                content: {
                    componentConfig: {
                        component: AiqDialogComponent,
                        componentData: { model: {} },
                    },
                },
                actions: {
                    dialogActions: [
                        {
                            code: 'cancel',
                            style: ButtonType.simple,
                            title: 'Cancel',
                        },
                        {
                            code: 'select',
                            style: ButtonType.raised,
                            color: 'primary',
                            title: 'Select',
                            manualClose: true,
                        },
                    ],
                },
                executeActionSignal: executeActionSignal,
                dialogSize: 'xl',
            },
        });
    }

    openAIFDialog(): MatDialogRef<AifDialogComponent, any> {
        const executeActionSignal = signal(null);
        return this.dialogService.openDialog({
            descriptor: {
                header: {
                    title: 'Fields',
                    showCloseButton: true,
                },
                content: {
                    componentConfig: {
                        component: AifDialogComponent,
                        componentData: { model: {} },
                    },
                },
                actions: {
                    dialogActions: [],
                },
                executeActionSignal: executeActionSignal,
                dialogSize: 'xl',
            },
        });
    }

    transactionAiButton(): Observable<string> {
        this._loadingService.setLoadingStatus(true);
        return this.transactionAiService.generateDescription().pipe(
            tap(() => {
                this._loadingService.setLoadingStatus(false);
            })
        );
    }
}
